import { useMemo } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";
import { MAX_STRING_LEN } from "../../../common/constants";
import { MAX_DESCRIPTION_LEN, MAX_DURATION, MAX_PRICE } from "./constants";
import useLabelTranslations from "./useLabelTranslations";
import { Offering } from "./types";
import useTranslations from "./useTranslations";
import messages from "./messages";

const requiredString = Yup.string().trim().max(MAX_STRING_LEN).required();

const useFormSchema = (params: { minPrice: number }) => {
  const { minPrice } = params;

  const labelTranslations = useLabelTranslations();
  const translations = useTranslations();
  const { formatMessage } = useIntl();

  const schema = useMemo(() => {
    const {
      labelDepositAmount,
      labelDescription,
      labelModalityName,
      labelOfferings,
      labelName,
      labelPrice,
      labelVisibility,
      labelDuration,
    } = labelTranslations;

    return Yup.object().shape({
      name: requiredString.label(labelName),
      modalityName: requiredString.label(labelModalityName),
      offerings: Yup.array()
        .required()
        .min(1)
        .of(Yup.mixed())
        .label(labelOfferings)
        .test(
          "public-package-private-offerings",
          labelTranslations.labelPublicPackagePrivateOfferings,
          function checkPublicPackagePrivateOfferings(selectedOfferings) {
            const { parent } = this;
            const packageVisibility = parent.visibility;
            if (packageVisibility !== "public") {
              return true;
            }

            const hasPrivateOffering = selectedOfferings?.some(
              (selectedOffering: Offering) => {
                return selectedOffering.visibility === "private";
              },
            );

            return !hasPrivateOffering;
          },
        ),
      description: Yup.string()
        .label(labelDescription)
        .trim()
        .required()
        .max(MAX_DESCRIPTION_LEN),
      visibility: requiredString.label(labelVisibility),
      price: Yup.number()
        .label(labelPrice)
        .positive()
        .moreThan(minPrice)
        .lessThan(MAX_PRICE)
        .optional(),
      durationMinutes: Yup.number()
        .label(labelDuration)
        .positive()
        .moreThan(0)
        .lessThan(MAX_DURATION)
        .optional(),
      marketingText: Yup.string()
        .label(labelTranslations.labelMarketingText)
        .trim()
        .max(1000)
        .required()
        .test("rich-text-not-empty", (value) => {
          const plain = value?.replace(/<[^>]*>/g, "").trim();
          return Boolean(plain);
        }),
      depositAmount: Yup.number()
        .label(labelDepositAmount)
        .positive()
        .required()
        .test(
          "deposit-less-than-price-or-offerings",
          function testDeposit(value) {
            const { parent } = this;
            const { price, offerings } = parent;

            if (!value) {
              return false;
            }

            if (price && typeof price === "number") {
              if (value > price) {
                return this.createError({
                  message: formatMessage(messages.depositAmountError, {
                    price,
                  }),
                });
              }
              return true;
            }

            if (
              !offerings ||
              !Array.isArray(offerings) ||
              offerings.length === 0
            ) {
              return this.createError({
                message: translations.selectOfferings,
              });
            }

            const totalPrice = offerings.reduce(
              (sum: number, offering: { price: number }) => {
                return sum + (offering.price || 0);
              },
              0,
            );

            if (value >= totalPrice) {
              return this.createError({
                message: formatMessage(messages.depositAmountError, {
                  price: totalPrice,
                }),
              });
            }

            return true;
          },
        ),
    });
  }, [
    formatMessage,
    labelTranslations,
    minPrice,
    translations.selectOfferings,
  ]);

  return schema;
};

export default useFormSchema;
